import Matter from "matter-js"
import { createPendant } from "."
import { createNewConstraint } from "./create-new-constraint"
import { MainProduct } from "../../types"

// @TODO: Decompose into following function
// const updateSinglePendant = () => {}
// const updateAllPendants = () => {}

export const updatePendants = (
  prevActivePendantProductVariants: string[], 
  activePendantProductVaraintIds: string[], 
  engine: Matter.Engine,
  mainProduct: MainProduct,
  activeMainProductVariantId: string,
) => {
  const { pendantProducts } = mainProduct.metafields
  const allBodies = Matter.Composite.allBodies(engine.world)

  const allConstraints = Matter.Composite.allConstraints(engine.world)
  let allPendants = allBodies.filter((body) => body.label.startsWith("pendant/"))
  let allChains = allBodies.filter((body) => body.label.startsWith("chain/"))

  if (mainProduct.metafields.type === 'Necklace') {
    allPendants = allPendants.sort((a, b) => a.position.x - b.position.x)
    allChains = allChains.sort((a, b) => a.position.x - b.position.x)
  }

  if (mainProduct.metafields.type === 'Bracelet') {
    const centerX = 400, centerY = 380;

    const getAdjustedAngle = (body: any) => {
      let angle = Math.atan2(body.position.y - centerY, body.position.x - centerX);
      if (angle < 0) angle += 2 * Math.PI;
      angle = (2 * Math.PI - angle + Math.PI/2 + Math.PI) % (2 * Math.PI);
      return angle;
    };

    allPendants = allPendants.sort((a, b) => {
      const angleA = getAdjustedAngle(a);
      const angleB = getAdjustedAngle(b);
      return angleA - angleB;
    });

    allChains = allChains.sort((a, b) => {
      const angleA = getAdjustedAngle(a);
      const angleB = getAdjustedAngle(b);
      return angleA - angleB;
    });
  }

  if (activePendantProductVaraintIds.length === prevActivePendantProductVariants.length) {
    activePendantProductVaraintIds.forEach((activePendantProductVaraintId, index) => {
      if (activePendantProductVaraintId !== prevActivePendantProductVariants[index]) {
        const prevPendant = allPendants[index]
        const prevLeftConstraint = allConstraints.find((constraint) => constraint.bodyB === prevPendant)!
        const prevRightConstraint = allConstraints.find((constraint) => constraint.bodyA === prevPendant)!
        const leftChain = prevLeftConstraint.bodyA!
        const rightChain = prevRightConstraint.bodyB!

        const newPendant = createPendant(
          prevPendant.position.x, prevPendant.position.y, pendantProducts, activePendantProductVaraintId, mainProduct
        )

        const newLeftConstraint = createNewConstraint(leftChain, newPendant, mainProduct, activeMainProductVariantId)
        const newRightConstraint = createNewConstraint(newPendant, rightChain, mainProduct, activeMainProductVariantId)
    
        Matter.Composite.remove(engine.world, [prevLeftConstraint, prevPendant, prevRightConstraint])
        Matter.Composite.add(engine.world, [newLeftConstraint, newPendant, newRightConstraint])
      }
    })
  } else {
    // Remove all previous pendants with their constraints
    // Connect neighbour chains of the previous pendants
    // Loop through new pendants and ingrave them to the neckalace accourding their newly calculated positions 

    Matter.Composite.remove(engine.world, allPendants)

    let bodiesToRemove: (Matter.Body | Matter.Constraint)[] = []
    const bodiesToAdd: Matter.Constraint[] = []
    const chainsToConnect: Matter.Body[][] = []

    allPendants.forEach(((prevPendant) => {
      const prevLeftConstraint = allConstraints.find((constraint) => constraint.bodyB === prevPendant)!
      const prevRightConstraint = allConstraints.find((constraint) => constraint.bodyA === prevPendant)!

      bodiesToRemove = [...bodiesToRemove, ...[prevLeftConstraint, prevPendant, prevRightConstraint]]

      const leftChain = prevLeftConstraint.bodyA!
      const rightChain = prevRightConstraint.bodyB!

      chainsToConnect.push([leftChain, rightChain])
    }))

    Matter.Composite.remove(engine.world, bodiesToRemove)

    chainsToConnect.forEach(([leftChain, rightChain]) => {
      const constraint = createNewConstraint(leftChain, rightChain, mainProduct, activeMainProductVariantId)
      Matter.Composite.add(engine.world, constraint)
    })

    Matter.Composite.add(engine.world, bodiesToAdd)

    if (mainProduct.metafields.type === 'Necklace') {
      const numPendants = activePendantProductVaraintIds.length;
      const totalChains = allChains.length;
      const gapFraction = 0.14;
      const gapChains = Math.floor(totalChains * gapFraction);
      const pendantPositionStep = gapChains + 1;
      const totalUsedChains = (numPendants - 1) * pendantPositionStep + 1;
      const emptyChains = totalChains - totalUsedChains;
      const leftPadding = Math.floor(emptyChains / 2);

      activePendantProductVaraintIds.forEach((activePendantProductVaraint, i) => {
        const chainIndex = leftPadding + i * pendantPositionStep;
        
        const leftChain = allChains[chainIndex];
        const prevConstraint = allConstraints.find(constraint => constraint.bodyA === leftChain)!;
        const rightChain = prevConstraint.bodyB!;
        
        const { x, y } = leftChain.position;
        
        const pendant = createPendant(x, y, pendantProducts, activePendantProductVaraint, mainProduct);
        
        const newLeftConstraint = createNewConstraint(leftChain, pendant, mainProduct, activeMainProductVariantId);
        const newRightConstraint = createNewConstraint(pendant, rightChain, mainProduct, activeMainProductVariantId);
        
        Matter.Composite.remove(engine.world, prevConstraint);
        Matter.Composite.add(engine.world, [newLeftConstraint, pendant, newRightConstraint]);
      });
    } else {
      const anglePerPendantWithGap = 60
      const fractionPerPendant = anglePerPendantWithGap / 360
      
      const numPendants = activePendantProductVaraintIds.length
      const totalFraction = numPendants * fractionPerPendant
      
      const pendantFractions = []
      for (let i = 0; i < numPendants; i++) {
        const fraction = 0.5 - totalFraction/2 + (i + 0.5) * fractionPerPendant
        pendantFractions.push(fraction)
      }
      
      const totalChains = allChains.length
      const chainIndices = pendantFractions.map(fraction => {
        let correctedFraction = fraction % 1
        if (correctedFraction < 0) correctedFraction += 1
        
        const rawIndex = correctedFraction * totalChains
        return Math.floor(rawIndex) % totalChains
      })
      
      chainIndices.forEach((chainIndex, i) => {
        const leftChain = allChains[chainIndex]
        const prevConstraint = allConstraints.find(constraint => constraint.bodyA === leftChain)!
        const rightChain = prevConstraint.bodyB!
        
        const { x, y } = leftChain.position
        
        const pendant = createPendant(x, y, pendantProducts, activePendantProductVaraintIds[i], mainProduct)
        
        const newLeftConstraint = createNewConstraint(leftChain, pendant, mainProduct, activeMainProductVariantId)
        const newRightConstraint = createNewConstraint(pendant, rightChain, mainProduct, activeMainProductVariantId)
        
        Matter.Composite.remove(engine.world, prevConstraint)
        Matter.Composite.add(engine.world, [newLeftConstraint, pendant, newRightConstraint])
      })
    }
  }
}
